<template>
  <div>
    <DxForm id="formularioLectura" ref="formInstance" :form-data="model" label-location="top"
      :show-colon-after-label="false" validation-group="validacionLectura">
      <!-- xs: 768, sm: 992, md:1200 -->
      <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />

      <DxGroupItem :col-count="6" :col-span="6">
        <DxSimpleItem :visible="props.tipoSuministro === TipoSuministroInmuebleEnum.Luz" 
          data-field="Lectura" editor-type="dxNumberBox" :col-span="3" :editor-options="consumoNumberOptions">
          <DxLabel :text="$t('inmueble.lectura.lecturaPunta')" />
          <DxRequiredRule :message="$t('inmueble.validations.lectura')" />
          <DxRangeRule :min="0" :message="$t('inmueble.validations.lectura_negativa')" />
        </DxSimpleItem>
        <DxSimpleItem :visible="props.tipoSuministro === TipoSuministroInmuebleEnum.Luz" 
          data-field="Lectura2" editor-type="dxNumberBox" :col-span="3" :editor-options="consumoNumberOptionsNullable">
          <DxLabel :text="$t('inmueble.lectura.lecturaLlano')" />
          <DxRangeRule :min="0" :message="$t('inmueble.validations.lectura_negativa')" />
        </DxSimpleItem>
        <DxSimpleItem :visible="props.tipoSuministro === TipoSuministroInmuebleEnum.Luz" 
          data-field="Lectura3" editor-type="dxNumberBox" :col-span="3" :editor-options="consumoNumberOptionsNullable">
          <DxLabel :text="$t('inmueble.lectura.lecturaValle')" />
          <DxRangeRule :min="0" :message="$t('inmueble.validations.lectura_negativa')" />
        </DxSimpleItem>
        <DxSimpleItem :visible="props.tipoSuministro !== TipoSuministroInmuebleEnum.Luz" 
          data-field="Lectura" editor-type="dxNumberBox" :col-span="3" :editor-options="consumoNumberOptions">
          <DxLabel :text="$t('inmueble.lectura.lectura')" />
          <DxRequiredRule :message="$t('inmueble.validations.lectura')" />
          <DxRangeRule :min="0" :message="$t('inmueble.validations.lectura_negativa')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Fecha" editor-type="dxDateBox" :col-span="3">
          <DxLabel :text="$t('inmueble.lectura.fecha')" />
          <DxRequiredRule :message="$t('inmueble.validations.fecha')" />
        </DxSimpleItem>
        <DxSimpleItem :col-span="6">
          <DocumentCard ref="documentoCard" :title="$t('inmueble.lectura.adjuntos')"
            :required="false" align="horizontal" layout="default" />
        </DxSimpleItem>
      </DxGroupItem>
    </DxForm>
  </div>
</template>

<script setup lang="ts">
import { messageBoxResult } from '@components/dialogs/message-box';
import { useModalBuilder, type ToolbarReference } from '@helpers/modal-building-helper';
import { type DxButton } from 'devextreme-vue';
import {
  DxForm,
  DxLabel,
  DxColCountByScreen,
  DxRequiredRule,
  DxRangeRule,
  DxSimpleItem,
  DxGroupItem
} from "devextreme-vue/form";
import { getCurrentInstance, onMounted, ref, type PropType } from 'vue';
import { useIntl } from '@services/i18n';
import validationEngine from "devextreme/ui/validation_engine";
import { useApi } from '@api/integration';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import { TipoSuministroInmuebleEnum, type AdjuntoLecturaInmuebleModel } from '@api/api';
import { AddLecturaInmueble } from '@/components/inmuebles/models/AddLecturaInmueble';
import DocumentCard from '@/.shared/components/document-card/DocumentCard.vue';

const api = useApi();
const formInstance = ref<DxForm>();
const intl = useIntl();
const props = defineProps({
  model: {
    type: Object as () => AddLecturaInmueble,
    required: false
  },
  inmuebleId: {
    type: String,
    required: true
  },
  tipoSuministro: {
    type: String as PropType<TipoSuministroInmuebleEnum>,
    required: true
  }
});
const documentoCard = ref<InstanceType<typeof DocumentCard>>();

const consumoNumberOptions = {
  showSpinButtons: true,
  step: 0.1,
  format: {
    type: "fixedPoint",
    precision: 2,
    formatter: (value: number) => {
      if (value != undefined) {
        const unidad = props.tipoSuministro === TipoSuministroInmuebleEnum.Agua ? 'm³' : 'kWh';
        return intl.i18n.n(Number(value.toFixed(3)), 'decimal') + ' ' + unidad;
      }
    }
  }
};

const consumoNumberOptionsNullable = {
  ...consumoNumberOptions,
  showClearButton: true,
  value: null
};

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)

  configureButtons(modal);
});

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  }
  );
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const getArchivos = () : AdjuntoLecturaInmuebleModel[] => {
  const cardFiles = documentoCard.value?.getFiles();
  const result: AdjuntoLecturaInmuebleModel[] = [];
  
  for (const fileId in cardFiles) {
    const file = cardFiles[fileId];
    result.push({
      Id: file.id,
      Nombre: file.fileName,
      Ruta: "", // Se completa al subir el fichero, pq se sube a un temp
    });

  }
  
  return result;
}

const subirFicheros = async () : Promise<AdjuntoLecturaInmuebleModel[] | null> => {
  const archivos : AdjuntoLecturaInmuebleModel[] = getArchivos();
  const cardFiles = documentoCard.value?.getFiles();

  const results = await Promise.allSettled(
    archivos.map(async (archivo) => {
      const file = cardFiles?.[archivo.Id];
      if (file != null) {
        let tempUrl = await api.callStandardApi("postDocumentoTemp", { showLoading: false }, archivo.Nombre, file.file!);
        archivo.Ruta = tempUrl;
        return archivo;
      }
      return null;
    })
  );

  const archivosSubidos: AdjuntoLecturaInmuebleModel[] = [];
  for (const result of results) {
    if (result.status === "fulfilled" && result.value !== null) {
      archivosSubidos.push(result.value);
    } else if (result.status === "rejected") {
      toastError(intl.i18n.t('inmueble.lectura.ui.error_subida_ficheros'));
      return null;
    }
  }

  return archivosSubidos;
}

const saveOrEdit = async () => {
  var res = validationEngine.validateGroup("validacionLectura");

  if (res.isValid ) {
    const resultadoSubidaFicheros = await subirFicheros();
    if (!resultadoSubidaFicheros) {
      return;
    }

    try {
      await api.callStandardApi("addLecturaInmueble", {showLoading: true}, props.inmuebleId, {...props.model!, Adjuntos: resultadoSubidaFicheros} );
      toastSuccess(intl.i18n.t('inmueble.lectura.ui.creacion_exito'));
      return true;
    } catch (error) {
      toastError(intl.i18n.t('inmueble.lectura.ui.creacion_fallo'));
      return false;
    }
  } else {
    toastError(intl.i18n.t('inmueble.lectura.ui.revise_validacion'));
  }
}

</script>